import { t } from "i18next";
import { z } from "zod";

import {
  AdHocPlaceOfSafetySchema,
  CuratedPlaceOfSafetySchema,
  dayjs,
  looseUuid,
  mustBeDefined,
  mustParse,
} from "@thalamos/common";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  dateTimeSchema,
  FormSectionSchema,
  longTextSchema,
  shortTextSchema,
  type RecordPlaceOfSafetyMinimumFields,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";
import { getLatestPlaceOfSafetyForm } from "../../workflowLogic/getLatestPlaceOfSafety.js";

// v1.1 shared form specification for Request Police Presence
export const formSpecification = new MultiPageFormBuilder()
  // added in v1.1 of Request Police Presence
  .withFieldNeverVisible({
    fieldName: "source",
    defaultValue: () => {
      return { value: "mhf" as const };
    },
    ...new FieldBuilder()
      .withNullableSchema(z.enum(["emha" as const, "mhf" as const]))
      .build(),
  })
  .withFieldNeverVisible({
    defaultValue: () => ({ value: dayjs.utc().toISOString() }),
    fieldName: "createdAt",
    ...new FieldBuilder().withNullableSchema(dateTimeSchema).build(),
  })
  .withFieldAlwaysVisible({
    fieldName: "hasPolicePresenceRequested",
    type: "radio",
    label: tk("requestPolicePresence.wereYouAskedToStay"),
    requiredLabel: tk("requestPolicePresence.wereYouAskedToStayRequired"),
    ...new FieldBuilder()
      .withSchema(
        z.enum(["yes" as const, "no" as const, "already-left" as const]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })

  // place of safety should be specified whether entered manually
  // or picked from the curated list
  .withFieldNeverVisible({
    defaultValue: ({ incidentWorkItem }) => {
      const parser = mustParse(
        z.union([CuratedPlaceOfSafetySchema, AdHocPlaceOfSafetySchema]),
      );

      return (
        getLatestPlaceOfSafetyForm(incidentWorkItem)
          .map(
            (pos) =>
              pos.sections.find(
                (s) => s.id === FormSectionSchema.enum.recordPlaceOfSafetyMain,
              )?.data,
          )
          .andThen(mustBeDefined())
          .map(
            (data) =>
              (data as unknown as RecordPlaceOfSafetyMinimumFields)
                .placeOfSafety,
          )
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
          .andThen((data) => parser(data as any))
          .unwrapOr(null)
      );
    },
    fieldName: "placeOfSafety",
    schema: z.union([CuratedPlaceOfSafetySchema, AdHocPlaceOfSafetySchema]),
  })

  // TODO: custom display component with link?
  .withReadonlyFieldAlwaysVisible({
    type: "typography",
    label: tk("requestPolicePresence.placeOfSafetyText"),
    textFn: ({ formValues }) => [formValues.placeOfSafety.name],
    fieldName: "placeOfSafetyText",
  })

  .withFieldNeverVisible({
    fieldName: "incidentPlaceOfSafetyId",
    defaultValue: ({ incidentWorkItem }) => {
      return getLatestPlaceOfSafetyForm(incidentWorkItem)
        .map((pos) => ({ value: pos.id }))
        .unwrapOr(null);
    },
    ...new FieldBuilder().withNullableSchema(looseUuid()).build(),
  })

  // added in v1.1 of Request Police Presence
  .withFieldNeverVisible({
    fieldName: "mhfUserId",
    defaultValue: ({ incidentWorkItem }) => {
      const currentOwner = incidentWorkItem?.participants.find(
        (p) => p.role === "owner",
      );
      return currentOwner ? { value: currentOwner.participantId } : null;
    },
    ...new FieldBuilder().withNullableSchema(looseUuid()).build(),
  })

  // added in v1.1 of Request Police Presence
  .withFieldNeverVisible({
    fieldName: "mhfUserName",
    defaultValue: ({ incidentWorkItem }) => {
      const currentOwner = incidentWorkItem?.participants.find(
        (p) => p.role === "owner",
      );
      return currentOwner ? { value: currentOwner.name } : null;
    },
    ...new FieldBuilder().withNullableSchema(shortTextSchema).build(),
  })

  .withFieldNeverVisible({
    defaultValue: () => null,
    fieldName: "emhaTeamId",
    ...new FieldBuilder().withNullableSchema(looseUuid()).build(),
  })
  .withFieldNeverVisible({
    defaultValue: () => null,
    fieldName: "emhaTeamName",
    ...new FieldBuilder().withNullableSchema(shortTextSchema).build(),
  })
  .withFieldNeverVisible({
    defaultValue: () => null,
    fieldName: "emhaUserId",

    ...new FieldBuilder().withNullableSchema(looseUuid()).build(),
  })
  .withFieldNeverVisible({
    defaultValue: () => null,
    fieldName: "emhaUserName",

    ...new FieldBuilder().withNullableSchema(shortTextSchema).build(),
  })
  .withFieldNeverVisible({
    defaultValue: () => null,
    fieldName: "emhaOrganisationId",

    ...new FieldBuilder().withNullableSchema(looseUuid()).build(),
  })
  .withFieldNeverVisible({
    defaultValue: () => null,
    fieldName: "emhaOrganisationName",

    ...new FieldBuilder().withNullableSchema(shortTextSchema).build(),
  })
  .withFieldConditionallyVisible({
    fieldName: "healthProfessionalName",
    type: "textbox",
    label: tk("requestPolicePresence.healthProfessionalName"),
    requiredLabel: tk("requestPolicePresence.healthProfessionalNameRequired"),
    placeholder: tk("requestPolicePresence.healthProfessionalNamePlaceholder"),
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.source?.value === "mhf" &&
      formValues.hasPolicePresenceRequested?.value === "yes",
  })
  .withFieldConditionallyVisible({
    fieldName: "policePresenceRequestedReason",
    type: "dropdown",
    label: tk("requestPolicePresence.policePresenceRequestedReason"),
    requiredLabel: tk(
      "requestPolicePresence.policePresenceRequestedReasonRequired",
    ),
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "immediateRiskToLifeOrOfSeriousHarm" as const,
          "notEnoughStaff" as const,
          "transfer" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk("requestPolicePresence.dropdown.notEnoughStaff"),
        value: "notEnoughStaff",
      },
      {
        label: tk(
          "requestPolicePresence.dropdown.immediateRiskToLifeOrOfSeriousHarm",
        ),
        value: "immediateRiskToLifeOrOfSeriousHarm",
      },
      {
        label: tk("requestPolicePresence.dropdown.transfer"),
        value: "transfer",
      },
      {
        label: tk("requestPolicePresence.dropdown.other"),
        value: "other",
      },
    ],
    isVisible: ({ formValues }) =>
      formValues.hasPolicePresenceRequested?.value === "yes",
  })
  .withFieldConditionallyVisible({
    fieldName: "furtherReasoning",
    label: tk("requestPolicePresence.furtherReasoning"),
    requiredLabel: tk("requestPolicePresence.furtherReasoningRequired"),
    type: "textbox",
    rows: "5",
    multiline: true,
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.hasPolicePresenceRequested?.value === "yes",
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "furtherReasoningAdvice",
    type: "banner",
    body: tk("requestPolicePresence.furtherReasoningAdvice"),
    bannerType: "warning",
    isVisible: ({ formValues }) =>
      formValues.hasPolicePresenceRequested?.value === "yes",
  })
  .withPage(["placeOfSafetyText", "hasPolicePresenceRequested"])
  .withPage(["healthProfessionalName"])
  .withPage([
    "policePresenceRequestedReason",
    "furtherReasoning",
    "furtherReasoningAdvice",
  ])
  .build({ t });

export type RequestPolicePresenceMainFormData = ReturnType<
  typeof formSpecification
>["formType"];
